/**
 * "Scrivi a piu' persone" su WhatsApp.
 *
 * Tre passi (destinatari, numero e messaggio, riepilogo) e poi il resoconto.
 * Niente invii dal browser: ogni destinatario diventa un messaggio programmato
 * scaglionato che il core spedisce (flusia-core queue/scheduledMessages.ts).
 * Si apre dalla lista chat (senza destinatari) o dalla lista Lead (con i Lead
 * selezionati). Visibile solo a chi vede almeno un numero WhatsApp.
 */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Send } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useBulkSend } from './useBulkSend';
import { BulkAudienceStep } from './BulkAudienceStep';
import { BulkMessageStep } from './BulkMessageStep';
import { BulkSummaryStep } from './BulkSummaryStep';
import { BulkSendsReport } from './BulkSendsReport';

type Step = 'destinatari' | 'messaggio' | 'riepilogo' | 'resoconti';

const STEPS: Array<{ id: Step; label: string }> = [
  { id: 'destinatari', label: 'Destinatari' },
  { id: 'messaggio', label: 'Numero e messaggio' },
  { id: 'riepilogo', label: 'Riepilogo' },
];

interface Props {
  onClose: () => void;
  /** Lead/Clienti gia' scelti (selezione nella lista Lead). */
  clientIds?: string[];
  /** Apre direttamente sui resoconti. */
  startOnReports?: boolean;
}

export function BulkSendModal({ onClose, clientIds, startOnReports = false }: Props) {
  const s = useBulkSend(clientIds);
  const [step, setStep] = useState<Step>(startOnReports ? 'resoconti' : 'destinatari');
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const count = s.built.recipients.length;
  const nextBlock: string | null =
    step === 'destinatari' ? (s.loadingCandidates ? 'Sto cercando i destinatari...' : count === 0 ? 'Nessun destinatario valido.' : null)
      : step === 'messaggio' ? (count === 0 ? 'Nessun destinatario valido con questo numero.' : s.messageBlock)
        : null;

  const conferma = async () => {
    setSubmitError(null);
    try {
      const id = await s.submit();
      setCreatedId(id);
      setStep('resoconti');
      toast.success(count === 1 ? 'Invio programmato per 1 persona' : `Invio programmato per ${count} persone`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Invio non riuscito. Riprova.');
    }
  };

  const noNumbers = !s.configsLoading && s.configs.length === 0;

  return createPortal(
    <div className="fixed inset-0 z-[9000] flex items-start justify-center bg-black/30 px-4 pt-[8vh]" onMouseDown={(e) => { if (e.target === e.currentTarget && !s.submitting) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label="Scrivi a piu' persone" className="w-full max-w-xl rounded-xl bg-white dark:bg-[#252528] shadow-2xl border border-[rgba(55,53,47,0.09)] dark:border-white/10 overflow-hidden flex flex-col max-h-[84vh]">
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold text-[#37352F] dark:text-white">Scrivi a piu' persone</h3>
            {/* Dopo un invio niente "Nuovo invio" con gli stessi dati: evita di rimandarlo per errore. */}
            {!createdId && (
              <button
                type="button"
                onClick={() => setStep(step === 'resoconti' ? 'destinatari' : 'resoconti')}
                className="text-xs text-[#2383E2] hover:underline"
              >
                {step === 'resoconti' ? 'Nuovo invio' : 'Invii precedenti'}
              </button>
            )}
          </div>
          <button type="button" onClick={onClose} disabled={s.submitting} className="p-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10" aria-label="Chiudi">
            <X className="w-4 h-4 text-[#787774]" />
          </button>
        </div>

        {step !== 'resoconti' && (
          <div className="flex gap-1 px-4 pb-2">
            {STEPS.map((st, i) => (
              <span key={st.id} className={`flex-1 text-[11px] pb-1 border-b-2 ${st.id === step ? 'border-[#2383E2] text-[#37352F] dark:text-white font-medium' : 'border-[rgba(55,53,47,0.09)] text-[#787774]'}`}>
                {i + 1}. {st.label}
              </span>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {noNumbers ? (
            <p className="text-sm text-[#787774] py-6 text-center">Non vedi nessun numero WhatsApp: chiedi a un amministratore di darti accesso.</p>
          ) : step === 'resoconti' && s.organizationId ? (
            <BulkSendsReport organizationId={s.organizationId} initialId={createdId} />
          ) : step === 'destinatari' ? (
            <BulkAudienceStep s={s} />
          ) : step === 'messaggio' ? (
            <BulkMessageStep s={s} />
          ) : (
            <BulkSummaryStep s={s} />
          )}
        </div>

        {step !== 'resoconti' && !noNumbers && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-[rgba(55,53,47,0.09)] dark:border-white/10">
            <span className="text-xs text-amber-700 dark:text-amber-300 min-w-0">{submitError || nextBlock || ''}</span>
            <div className="flex gap-2 flex-shrink-0">
              {step !== 'destinatari' && (
                <button type="button" disabled={s.submitting} onClick={() => setStep(step === 'riepilogo' ? 'messaggio' : 'destinatari')} className="px-3 py-1.5 rounded-md text-sm text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10">
                  Indietro
                </button>
              )}
              {step !== 'riepilogo' ? (
                <button type="button" disabled={!!nextBlock} onClick={() => setStep(step === 'destinatari' ? 'messaggio' : 'riepilogo')} className="px-3 py-1.5 rounded-md text-sm font-medium bg-[#2383E2] text-white disabled:opacity-40">
                  Avanti
                </button>
              ) : (
                <button type="button" disabled={s.submitting || count === 0 || !!s.messageBlock} onClick={conferma} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40">
                  {s.submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {count === 1 ? 'Conferma e invia a 1 persona' : `Conferma e invia a ${count} persone`}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
